/**
 * Maps DOM elements to PDP ScreenElement format.
 * Identifies interactive elements and classifies their types.
 * Captures rich metadata: options, validation, grouping, containers.
 */

import { generateSelector } from './selector-gen.js';
import { DROPDOWN_OPTION_SELECTORS } from '../automation/actions.js';
import {
  computeNetworkStatus as computeNetworkStatusFromShim,
  drainAmbientSignals as drainAmbientSignalsFromShim,
  drainNetworkEventsSince as drainNetworkEventsSinceFromShim,
  getLastNetworkEventId as getLastNetworkEventIdFromShim,
} from './network-events.js';

// ─── Shared Types ──────────────────────────────────────────────────────────

export interface ValidationAttrs {
  required?: boolean;
  min?: string;
  max?: string;
  step?: string;
  pattern?: string;
  minlength?: number;
  maxlength?: number;
}

export interface OptionEntry {
  value: string;
  label: string;
}

export interface FormField {
  name: string;
  type: string;
  label: string;
  selector?: string;
  options?: OptionEntry[];
  validation?: ValidationAttrs;
  checked?: boolean;
  groupName?: string;
}

export interface MappedElement {
  type: string;
  selector: string;
  label: string;
  tag: string;
  description?: string;
  inputType?: string;
  options?: OptionEntry[];
  validation?: ValidationAttrs;
  checked?: boolean;
  groupName?: string;
  containerType?: string;
  containerLabel?: string;
  /**
   * `aria-describedby` text — typically field help ("must be a valid email",
   * "minimum 8 characters with one number"). Surfaces field requirements
   * to the agent so it can fill realistically and explain what each field
   * expects. P1.
   */
  help?: string;
  /**
   * Effective disabled state, including ancestor cascade — disabled
   * <fieldset>, [aria-disabled=true] container, etc. The flat
   * INTERACTIVE_SELECTORS already drops `button:not([disabled])` but misses
   * inputs/selects inside disabled fieldsets; this surfaces the truth so
   * the agent doesn't try to click a greyed-out submit. P1.
   */
  disabled?: boolean;
  /**
   * `aria-busy=true` ancestor — content is loading and likely to mutate.
   * The agent should defer interactions until busy clears. P1.
   */
  busy?: boolean;
  /**
   * Whether the element is currently within the viewport rect. The agent
   * uses this to decide whether to scroll first or click directly. P1
   * (#21).
   */
  inViewport?: boolean;
  /**
   * Pixels below the fold (positive when below, negative when above
   * viewport, 0 when in-view). Lets the agent size its scroll. P1.
   */
  pixelsBelowFold?: number;
  /**
   * True when another element overlays this one at its center point —
   * a click would land on the overlay instead. Detected via
   * `document.elementFromPoint(rect.cx, rect.cy)`. P1 (#22).
   */
  occluded?: boolean;
  /**
   * Containing landmark (nav / main / aside / header / footer / region) —
   * lets the agent talk about location semantically ("in the right
   * sidebar") instead of "near element 42". P1 (#12).
   */
  landmark?: string;
  /**
   * Tab index — the keyboard-focus ordering. -1 means "not focusable by
   * keyboard"; 0 = natural tab order; >0 = explicit override. The agent
   * uses this to plan keyboard-driven interactions. P1 (#11).
   */
  tabIndex?: number;
  /**
   * Phase 2 (#A1, #A5). Visual error styling on inputs/textareas: red
   * border, red outline, red box-shadow ring, or an error-text sibling.
   * Surfaces silent validation rejections that don't set `aria-invalid`
   * — the dominant pattern in modern Tailwind/UI-kit forms.
   */
  visualErrorSignal?: boolean;
  /**
   * Phase 2 (#A2). Button "loading" state: spinner-inside-button,
   * `data-loading="true"`, `aria-busy="true"` on the element itself,
   * `cursor: progress`, or text-replaced-with-dots/spinner. Lets the
   * agent see "save is syncing" instead of "save did nothing".
   */
  loadingSignal?: boolean;
  /**
   * Phase 2 (#A6, #A12). Computed interactability hint:
   *   ok               — clicking will fire events as expected
   *   disabled_visual  — visually greyed out (cursor:not-allowed / opacity)
   *                       even when no `disabled` / `aria-disabled` attr
   *   pointer_blocked  — `pointer-events: none` (often :has()-driven)
   *   transform_hidden — visually present but transformed off-screen
   *                       (scale(0), clip-path inset 100%)
   *
   * The agent uses this to avoid silent no-op clicks the verifier would
   * later flag, and to narrate WHY ("the Save button is greyed out
   * because the email field is empty").
   */
  interactabilityHint?: 'ok' | 'disabled_visual' | 'pointer_blocked' | 'transform_hidden';
}

/**
 * Heading outline tree. Mirrors the H1→H6 nesting on the page so the
 * agent can speak about hierarchy ("we're in the Billing section under
 * Settings") rather than reading a flat list. P1 (#13).
 */
export interface HeadingNode {
  level: number;
  text: string;
  children: HeadingNode[];
}

/**
 * Landmark map — one entry per <nav>, <main>, <aside>, <header>,
 * <footer>, [role=region], [role=navigation], [role=complementary]. P1 (#12).
 */
export interface LandmarkInfo {
  role: string;
  label: string;
  selector: string;
  rect: { x: number; y: number; w: number; h: number };
}

export interface PageSnapshot {
  urlPath: string;
  pageTitle: string;
  headings: string[];
  /**
   * Hierarchical heading outline (H1→H6). Same content as `headings` but
   * preserves nesting. P1 (#13).
   */
  headingTree?: HeadingNode[];
  elements: MappedElement[];
  /**
   * True iff `elements` was capped — there are more interactive elements
   * on the page than the snapshot is shipping. The agent should ask the
   * visitor what they're looking for or scroll/filter rather than assume
   * what they see is everything. P1 (#3).
   */
  elementsTruncated?: boolean;
  /**
   * Total interactive-element count on the page (uncapped). When this is
   * far above `elements.length`, virtualization or pagination is likely.
   * P1 (#3, #4).
   */
  totalInteractiveCount?: number;
  /**
   * Heuristic: did we detect a virtualized list / table on the page?
   * (`react-window`, `react-virtualized`, `cdk-virtual-scroll-viewport`,
   * or `aria-rowcount` exceeding visible row count.) When true the agent
   * should scroll-to-find rather than click-by-index. P1 (#4).
   */
  hasVirtualization?: boolean;
  navigation: Array<{ text: string; href: string }>;
  forms: Array<{ id: string; fields: FormField[] }>;
  tables: Array<{ headers: string[]; rowCount: number }>;
  cards: Array<{ selector: string; label: string; elements: string[] }>;
  popovers: Array<{ triggerSelector: string; content: string }>;
  fieldGroups: Array<{
    name: string;
    type: 'radio' | 'checkbox';
    label: string;
    options: Array<{ value: string; label: string; checked: boolean }>;
  }>;
  viewport: { width: number; height: number; scrollX: number; scrollY: number };
  openModals: ModalSnapshot[];
  /**
   * Page landmarks (`<nav>`, `<main>`, `<aside>`, `<header>`, `<footer>`,
   * `role=region`). Gives the agent a semantic map of the page. P1 (#12).
   */
  landmarks?: LandmarkInfo[];
  /**
   * Same-origin nested iframes (cross-origin iframes are listed too but
   * marked `opaque: true` because the browser blocks reads). P1 (#2).
   */
  iframes?: Array<{ src: string; selector: string; opaque: boolean }>;
}

export interface ModalSnapshot {
  trigger: string;
  title: string;
  elements: MappedElement[];
  forms: Array<{ id: string; fields: FormField[] }>;
}

const INTERACTIVE_SELECTORS = [
  'button:not([disabled])',
  'a[href]',
  'input:not([type="hidden"])',
  'select',
  'textarea',
  '[role="button"]',
  '[role="tab"]',
  '[role="link"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[role="switch"]',
  '[role="combobox"]',
  '[role="menuitem"]',
  // Dropdown triggers that aren't formally combobox-roled. Most modern UI
  // libraries use a <button aria-haspopup="listbox"> trigger that
  // portal-mounts the listbox on click; without these in the interactive
  // pass the agent saw a button and called `click` instead of the
  // dropdown-aware `select_option`.
  '[aria-haspopup="listbox"]',
  '[aria-haspopup="menu"]',
  '[aria-haspopup="true"]',
  '[data-radix-select-trigger]',
  '[data-headlessui-state]',
  '[data-testid]',
  '[data-demo-id]',
];

const SPECIFIC_INPUT_TYPES = [
  'email', 'date', 'datetime-local', 'time', 'number', 'tel',
  'url', 'color', 'file', 'range', 'password', 'search',
];

// ─── Shadow-DOM-aware traversal (P1) ───────────────────────────────────────

/**
 * Walk every host that has an open shadowRoot reachable from `root`. Closed
 * shadow roots are unreachable by design — those stay invisible. Iterative
 * to avoid blowing the stack on deep component trees. Returns each shadow
 * root in document order so callers can run their own queries inside.
 *
 * Modern design systems (Salesforce LWC, Stencil, Lit, Radix Primitives that
 * lift to portals, Firebase auth widgets) put their interactive content
 * inside shadow roots. The classic `document.querySelectorAll(sel)` never
 * pierces those boundaries — meaning the snapshot is missing whole
 * sub-trees of UI. P1.
 */
function collectShadowRoots(root: Document | ShadowRoot): ShadowRoot[] {
  const out: ShadowRoot[] = [];
  // TreeWalker is dramatically faster than querySelectorAll('*') on a
  // 5000+ element tree and skips text/comment nodes for free.
  const walker = document.createTreeWalker(
    root === document ? document.documentElement : (root as ShadowRoot),
    NodeFilter.SHOW_ELEMENT,
  );
  let node = walker.nextNode();
  while (node) {
    const sr = (node as Element).shadowRoot;
    if (sr) {
      out.push(sr);
      // Recurse — nested shadow roots are real (Lit composing Lit, etc.).
      out.push(...collectShadowRoots(sr));
    }
    node = walker.nextNode();
  }
  return out;
}

/**
 * Shadow-DOM-piercing variant of `document.querySelectorAll`. Runs the
 * selector against the document AND every reachable open shadow root, in
 * document order. Same selector semantics — callers don't need to know
 * about shadow boundaries.
 *
 * Use sparingly per traversal: this iterates every shadow root, so calling
 * it inside a tight loop multiplies cost. Prefer caching the shadow-root
 * list when running a list of selectors against the same DOM.
 */
function querySelectorAllDeep(selector: string): Element[] {
  const out: Element[] = [];
  try {
    document.querySelectorAll(selector).forEach((el) => out.push(el));
  } catch { /* malformed selector — skip */ }
  for (const sr of collectShadowRoots(document)) {
    try {
      sr.querySelectorAll(selector).forEach((el) => out.push(el));
    } catch { /* skip */ }
  }
  return out;
}

// ─── Effective state helpers (P1) ──────────────────────────────────────────

/**
 * True when `el` is effectively disabled because of an ancestor — disabled
 * <fieldset>, [aria-disabled=true] / [data-disabled=true] container. The
 * direct disabled attribute is read at call sites; this only walks up.
 */
function hasDisabledAncestor(el: Element): boolean {
  let parent: Element | null = el.parentElement;
  while (parent && parent !== document.body) {
    if (parent.tagName === 'FIELDSET' && (parent as HTMLFieldSetElement).disabled) return true;
    if (parent.getAttribute('aria-disabled') === 'true') return true;
    if (parent.getAttribute('data-disabled') === 'true') return true;
    parent = parent.parentElement;
  }
  return false;
}

/** True when any ancestor declares `aria-busy=true`. */
function hasBusyAncestor(el: Element): boolean {
  let parent: Element | null = el;
  while (parent && parent !== document.body) {
    if (parent.getAttribute('aria-busy') === 'true') return true;
    parent = parent.parentElement;
  }
  return false;
}

/**
 * Resolve `aria-labelledby` (multi-ref). Returns the concatenated, trimmed
 * text of every referenced element, joined by spaces. Empty string when no
 * labelledby is set or none of the refs resolve. P1.
 */
function resolveAriaLabelledBy(el: Element): string {
  const ref = el.getAttribute('aria-labelledby');
  if (!ref) return '';
  const ids = ref.split(/\s+/).filter(Boolean);
  const parts: string[] = [];
  for (const id of ids) {
    const target = document.getElementById(id);
    if (target) {
      const text = (target as HTMLElement).innerText?.trim() ?? target.textContent?.trim() ?? '';
      if (text) parts.push(text);
    }
  }
  return parts.join(' ').trim();
}

/**
 * Resolve `aria-describedby` to its concatenated text. Used to surface
 * field help text ("must be 8+ chars with one number") to the agent so
 * it knows what valid input looks like. P1.
 */
function resolveAriaDescribedBy(el: Element): string {
  const ref = el.getAttribute('aria-describedby');
  if (!ref) return '';
  const ids = ref.split(/\s+/).filter(Boolean);
  const parts: string[] = [];
  for (const id of ids) {
    const target = document.getElementById(id);
    if (target) {
      const text = (target as HTMLElement).innerText?.trim() ?? target.textContent?.trim() ?? '';
      if (text) parts.push(text);
    }
  }
  return parts.join(' ').trim().slice(0, 200);
}

// ─── Main Capture Functions ────────────────────────────────────────────────

/**
 * Capture a full snapshot of the current page.
 */
export function capturePageSnapshot(): PageSnapshot {
  const { elements, total, truncated } = captureInteractiveElementsRich();
  return {
    urlPath: location.pathname,
    pageTitle: document.title,
    headings: captureHeadings(),
    headingTree: captureHeadingTree(),
    elements,
    elementsTruncated: truncated,
    totalInteractiveCount: total,
    hasVirtualization: detectVirtualization(),
    navigation: captureNavigation(),
    forms: captureForms(),
    tables: captureTables(),
    cards: captureCards(),
    popovers: capturePopovers(),
    fieldGroups: captureFieldGroups(),
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
      scrollX: window.scrollX,
      scrollY: window.scrollY,
    },
    openModals: captureOpenModals(),
    landmarks: captureLandmarks(),
    iframes: captureIframes(),
  };
}

// ─── Landmarks, iframes, virtualization, heading tree (P1) ────────────────

const LANDMARK_TAGS = ['nav', 'main', 'aside', 'header', 'footer'] as const;
const LANDMARK_ROLES = ['navigation', 'main', 'complementary', 'banner', 'contentinfo', 'region'] as const;

function captureLandmarks(): LandmarkInfo[] {
  const out: LandmarkInfo[] = [];
  const seen = new Set<Element>();
  for (const tag of LANDMARK_TAGS) {
    for (const el of querySelectorAllDeep(tag)) {
      if (seen.has(el) || !isVisible(el as HTMLElement)) continue;
      seen.add(el);
      const rect = (el as HTMLElement).getBoundingClientRect();
      out.push({
        role: tag,
        label: el.getAttribute('aria-label') || el.getAttribute('aria-labelledby')
          ? resolveAriaLabelledBy(el) || el.getAttribute('aria-label') || ''
          : '',
        selector: generateSelector(el),
        rect: { x: Math.round(rect.left), y: Math.round(rect.top), w: Math.round(rect.width), h: Math.round(rect.height) },
      });
    }
  }
  for (const role of LANDMARK_ROLES) {
    for (const el of querySelectorAllDeep(`[role="${role}"]`)) {
      if (seen.has(el) || !isVisible(el as HTMLElement)) continue;
      seen.add(el);
      const rect = (el as HTMLElement).getBoundingClientRect();
      out.push({
        role,
        label: el.getAttribute('aria-label') || resolveAriaLabelledBy(el) || '',
        selector: generateSelector(el),
        rect: { x: Math.round(rect.left), y: Math.round(rect.top), w: Math.round(rect.width), h: Math.round(rect.height) },
      });
    }
  }
  return out.slice(0, 20);
}

/**
 * List nested iframes. We can read same-origin iframe src; cross-origin
 * are listed as opaque region landmarks so the agent knows that a chunk
 * of UI exists but is unreachable. P1 (#2).
 */
function captureIframes(): Array<{ src: string; selector: string; opaque: boolean }> {
  const out: Array<{ src: string; selector: string; opaque: boolean }> = [];
  for (const frame of querySelectorAllDeep('iframe')) {
    const iframe = frame as HTMLIFrameElement;
    if (!isVisible(iframe)) continue;
    let opaque = true;
    try {
      // Cross-origin reads throw; same-origin returns the document.
      void iframe.contentDocument;
      opaque = iframe.contentDocument === null;
    } catch { opaque = true; }
    out.push({
      src: iframe.src || '',
      selector: generateSelector(iframe),
      opaque,
    });
    if (out.length >= 8) break;
  }
  return out;
}

/**
 * Heuristic virtualization detection. Looks for the most common library
 * markers + an aria-rowcount mismatch. False-positives are cheap (the
 * agent just scrolls-to-find instead of clicking by index); false-
 * negatives are expensive (clicks miss). Bias toward true. P1 (#4).
 */
function detectVirtualization(): boolean {
  // Library-specific markers.
  if (querySelectorAllDeep(
    '[data-react-window], [data-virtuoso-scroller], [class*="ReactVirtualized"], ' +
    '[class*="VirtualScroll"], cdk-virtual-scroll-viewport, [class*="virtualized"]',
  ).length > 0) return true;
  // aria-rowcount > visible row count is the spec-correct signal for
  // virtualized grids.
  for (const grid of querySelectorAllDeep('[role="grid"], [role="treegrid"]')) {
    const declared = Number(grid.getAttribute('aria-rowcount') || '0');
    const visible = grid.querySelectorAll('[role="row"]').length;
    if (declared > 0 && declared > visible * 1.5) return true;
  }
  return false;
}

function captureHeadingTree(): HeadingNode[] {
  const flat: HeadingNode[] = [];
  for (const h of querySelectorAllDeep('h1, h2, h3, h4, h5, h6')) {
    if (!isVisible(h as HTMLElement)) continue;
    const text = (h as HTMLElement).innerText?.trim();
    if (!text) continue;
    const level = Number(h.tagName.slice(1));
    flat.push({ level, text: text.slice(0, 120), children: [] });
  }
  // Build the tree by walking flat in document order; each node's parent
  // is the most recent ancestor with a strictly smaller level.
  const root: HeadingNode[] = [];
  const stack: HeadingNode[] = [];
  for (const node of flat) {
    while (stack.length > 0 && stack[stack.length - 1].level >= node.level) stack.pop();
    if (stack.length === 0) root.push(node);
    else stack[stack.length - 1].children.push(node);
    stack.push(node);
  }
  return root;
}

/**
 * Detect and capture any open modals/dialogs/drawers.
 */
export function captureOpenModals(): ModalSnapshot[] {
  const modals: ModalSnapshot[] = [];
  const modalSelectors = [
    '[role="dialog"]',
    '[aria-modal="true"]',
    'dialog[open]',
    '[class*="modal"][class*="open"]',
    '[class*="modal"][class*="show"]',
    '[class*="drawer"][class*="open"]',
    '[class*="overlay"][class*="active"]',
    '[class*="dialog"][class*="open"]',
  ];

  const seen = new Set<Element>();

  for (const sel of modalSelectors) {
    // Modal portals frequently render INTO a shadow root attached to
    // <body> by component libraries — captureOpenModals previously missed
    // every such modal. querySelectorAllDeep restores them.
    for (const el of querySelectorAllDeep(sel)) {
      if (seen.has(el) || !isVisible(el as HTMLElement)) continue;
      seen.add(el);

      const title =
        el.getAttribute('aria-label') ||
        el.querySelector('h1, h2, h3, [class*="title"]')?.textContent?.trim() ||
        'Untitled modal';

      const elements: MappedElement[] = [];
      const innerSeen = new Set<Element>();

      for (const iSel of INTERACTIVE_SELECTORS) {
        el.querySelectorAll(iSel).forEach((innerEl) => {
          if (innerSeen.has(innerEl) || !isVisible(innerEl as HTMLElement)) return;
          innerSeen.add(innerEl);
          const mapped = mapElement(innerEl);
          if (mapped) elements.push(mapped);
        });
      }

      const forms = captureFormsIn(el);

      modals.push({
        trigger: generateSelector(el),
        title: title.slice(0, 100),
        elements: elements.slice(0, 50),
        forms,
      });
    }
  }

  return modals;
}

// ─── Element Mapping ───────────────────────────────────────────────────────

/** Per-element cap when the page has a lot of interactive surface. The cap
 *  applies AFTER viewport prioritization so the agent always gets the
 *  in-viewport elements first. P1 (#3). */
const ELEMENT_BUDGET_VISIBLE = 80;
const ELEMENT_BUDGET_OFFSCREEN = 40;

function captureInteractiveElementsRich(): {
  elements: MappedElement[];
  total: number;
  truncated: boolean;
} {
  const seen = new Set<Element>();
  // Bucket pass 1 — collect all (deduped) candidates from every selector.
  const candidates: Element[] = [];
  for (const sel of INTERACTIVE_SELECTORS) {
    for (const el of querySelectorAllDeep(sel)) {
      if (seen.has(el) || !isVisible(el as HTMLElement)) continue;
      seen.add(el);
      candidates.push(el);
    }
  }

  // Bucket pass 2 — split into in-viewport and off-viewport, preserving
  // document order. The agent overwhelmingly cares about what the visitor
  // can see right now; off-viewport is a secondary tier.
  const visible: Element[] = [];
  const offscreen: Element[] = [];
  for (const el of candidates) {
    const rect = (el as HTMLElement).getBoundingClientRect();
    const inV = rect.top < window.innerHeight && rect.bottom > 0
      && rect.left < window.innerWidth && rect.right > 0;
    if (inV) visible.push(el);
    else offscreen.push(el);
  }

  const out: MappedElement[] = [];
  for (const el of visible.slice(0, ELEMENT_BUDGET_VISIBLE)) {
    const mapped = mapElement(el);
    if (mapped) out.push(mapped);
  }
  for (const el of offscreen.slice(0, ELEMENT_BUDGET_OFFSCREEN)) {
    const mapped = mapElement(el);
    if (mapped) out.push(mapped);
  }

  const total = candidates.length;
  const cap = ELEMENT_BUDGET_VISIBLE + ELEMENT_BUDGET_OFFSCREEN;
  return { elements: out, total, truncated: total > cap };
}

// Backwards-compatible export for the old call sites — route to the rich
// helper but only return the elements array.
function captureInteractiveElements(): MappedElement[] {
  return captureInteractiveElementsRich().elements;
}

function mapElement(el: Element): MappedElement | null {
  const htmlEl = el as HTMLElement;
  const tag = el.tagName.toLowerCase();
  const label = getLabel(htmlEl);
  if (!label || label.length > 100) return null;

  const mapped: MappedElement = {
    type: inferType(el),
    selector: generateSelector(el),
    label,
    tag,
  };

  // Specific input type
  if (tag === 'input') {
    const inputType = el.getAttribute('type') || 'text';
    if (inputType !== 'text') mapped.inputType = inputType;
  }

  // Select options (native)
  if (tag === 'select') {
    mapped.options = captureSelectOptions(el as HTMLSelectElement);
  }

  // Custom dropdown options. Try whenever this element is classified as a
  // select-style trigger (combobox role, aria-haspopup, library data attrs)
  // — works only when the listbox is already mounted in the DOM (i.e. the
  // dropdown is currently open at observation time). For lazy-mounted
  // dropdowns the executor returns the available options on the failure
  // path so the agent can retry.
  if (mapped.type === 'select' && tag !== 'select') {
    const opts = captureCustomDropdownOptions(el);
    if (opts) mapped.options = opts;
  }

  // Validation attributes
  const validation = captureValidation(el);
  if (validation) mapped.validation = validation;

  // Checked state
  if (tag === 'input') {
    const inputEl = el as HTMLInputElement;
    if (inputEl.type === 'checkbox' || inputEl.type === 'radio') {
      mapped.checked = inputEl.checked;
      const name = el.getAttribute('name');
      if (name) mapped.groupName = name;
    }
  }
  const role = el.getAttribute('role');
  if (role === 'checkbox' || role === 'switch' || role === 'radio') {
    mapped.checked = el.getAttribute('aria-checked') === 'true';
  }

  // Card container detection
  const card = el.closest('[class*="card"], article, [role="article"], [class*="Card"]');
  if (card && card !== el) {
    mapped.containerType = 'card';
    const cardLabel = getCardLabel(card);
    if (cardLabel) mapped.containerLabel = cardLabel;
  }

  // P1: aria-describedby help text — usually the field's "must be 8+ chars"
  // hint. Lets the agent fill realistic values that pass validation
  // instead of guessing and then handling the error.
  const help = resolveAriaDescribedBy(el);
  if (help) mapped.help = help;

  // P1: effective disabled cascade — input inside <fieldset disabled> or
  // [aria-disabled=true]. Stops the agent from clicking a greyed-out button.
  const directDisabled =
    (htmlEl as HTMLInputElement).disabled === true ||
    el.getAttribute('aria-disabled') === 'true';
  if (directDisabled || hasDisabledAncestor(el)) mapped.disabled = true;

  // P1: aria-busy cascade — content is loading; agent should defer.
  if (hasBusyAncestor(el)) mapped.busy = true;

  // P1: viewport awareness (#21). Lets the agent decide between
  // click-direct vs. scroll-then-click without a separate query.
  const rect = htmlEl.getBoundingClientRect();
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const inV = rect.top < window.innerHeight && rect.bottom > 0
    && rect.left < window.innerWidth && rect.right > 0;
  if (inV) {
    mapped.inViewport = true;
    mapped.pixelsBelowFold = 0;
  } else {
    mapped.inViewport = false;
    mapped.pixelsBelowFold = Math.round(rect.top - window.innerHeight);
  }

  // P1: occlusion detection (#22). If a different element occupies the
  // center point, a click here would land on the overlay. Only run for
  // visible elements — calling elementFromPoint at off-screen coords
  // returns null and would always-flag.
  if (inV) {
    try {
      const top = document.elementFromPoint(cx, cy);
      if (top && top !== el && !el.contains(top) && !top.contains(el)) {
        mapped.occluded = true;
      }
    } catch { /* ignore */ }
  }

  // P1: landmark (#12). Walk up to the nearest landmark and tag.
  const landmarkEl = el.closest('nav, main, aside, header, footer, [role=navigation], [role=main], [role=complementary], [role=banner], [role=contentinfo], [role=region]');
  if (landmarkEl) {
    const role = landmarkEl.getAttribute('role') || landmarkEl.tagName.toLowerCase();
    mapped.landmark = role;
  }

  // P1: tab index (#11). Native interactive elements default to 0;
  // div/span only get focus when tabindex is explicit.
  const ti = htmlEl.tabIndex;
  if (typeof ti === 'number' && ti !== 0) mapped.tabIndex = ti;

  // Phase 2: visual signals. Computed-style probe — runs only for
  // candidate elements (inputs/buttons/role=button|menuitem|link). The
  // signals are coarse on purpose: we want to flag "the visitor sees a
  // problem" not "what the exact CSS selector is."
  const signals = computeStyleSignals(htmlEl, mapped.type, mapped.disabled === true);
  if (signals.visualErrorSignal) mapped.visualErrorSignal = true;
  if (signals.loadingSignal) mapped.loadingSignal = true;
  if (signals.interactabilityHint && signals.interactabilityHint !== 'ok') {
    mapped.interactabilityHint = signals.interactabilityHint;
  }

  return mapped;
}

// ─── Phase 2: visual signal probe ─────────────────────────────────────────

/**
 * Coarse "is this element visually in trouble" detector. Runs once per
 * mapped element. We deliberately stay coarse — the cost of a false
 * positive (agent narrates a visible-error that wasn't really an error) is
 * far lower than a false negative (agent says "saved!" while a red border
 * screams at the visitor).
 *
 * NOTE: getComputedStyle is read-only — no layout thrash beyond the read
 * itself. We batch via the natural traversal order; no rAF needed for the
 * sub-100-element budget per snapshot.
 */
const ERROR_COLOR_RE =
  /(rgb\(\s*2(2[0-9]|3[0-9]|4[0-9]|5[0-5])\s*,\s*([0-9]|[1-7][0-9])\s*,\s*([0-9]|[1-7][0-9])\s*\)|#(e|f)[0-9a-f][0-9a-f][0-3][0-9a-f][0-3][0-9a-f]|red\b)/i;
const SPINNER_CLASS_RE = /\b(spin|spinner|loader|loading|progress)\b/i;
const ERROR_TEXT_SIBLING_RE = /\b(error|invalid|required|wrong|incorrect|must)\b/i;

function computeStyleSignals(
  el: HTMLElement,
  type: string,
  alreadyDisabled: boolean,
): {
  visualErrorSignal: boolean;
  loadingSignal: boolean;
  interactabilityHint: 'ok' | 'disabled_visual' | 'pointer_blocked' | 'transform_hidden';
} {
  const out = {
    visualErrorSignal: false,
    loadingSignal: false,
    interactabilityHint: 'ok' as 'ok' | 'disabled_visual' | 'pointer_blocked' | 'transform_hidden',
  };
  let style: CSSStyleDeclaration;
  try {
    style = getComputedStyle(el);
  } catch {
    return out;
  }

  // Visibility-hiding via transform / clip-path. Cheap to detect, very
  // common in modal-transition components that "render but hide" until
  // open. Element is in the DOM but visitor sees nothing.
  if (style.transform.includes('matrix(0') || style.clipPath?.includes('inset(100%)')) {
    out.interactabilityHint = 'transform_hidden';
  }

  // pointer-events:none — modern :has()-driven disable patterns. Click
  // would silently no-op even though there's no `disabled` / `aria-disabled`.
  if (style.pointerEvents === 'none' && !alreadyDisabled) {
    out.interactabilityHint = 'pointer_blocked';
  }

  // cursor: not-allowed → visually disabled even when no aria/attr.
  if (
    out.interactabilityHint === 'ok' &&
    !alreadyDisabled &&
    (style.cursor === 'not-allowed' || (parseFloat(style.opacity) < 0.5 && /button|link|menuitem/.test(type)))
  ) {
    out.interactabilityHint = 'disabled_visual';
  }

  // cursor: progress / wait → mid-action loading. Pair with spinner-child.
  if (style.cursor === 'progress' || style.cursor === 'wait') {
    out.loadingSignal = true;
  }
  // aria-busy on the element itself (not just an ancestor — that's `busy`).
  if (el.getAttribute('aria-busy') === 'true') out.loadingSignal = true;
  // data-loading / data-state="loading" — common in shadcn-ui / Radix.
  if (el.getAttribute('data-loading') === 'true' || el.getAttribute('data-state') === 'loading') {
    out.loadingSignal = true;
  }
  // Spinner descendant (any svg or div whose class hints at spin/spinner).
  if (!out.loadingSignal) {
    const spinner = el.querySelector('[class*="spin" i], [class*="loader" i], svg[class*="animate-spin"]');
    if (spinner) out.loadingSignal = true;
    else {
      const cls = el.getAttribute('class') || '';
      if (SPINNER_CLASS_RE.test(cls)) out.loadingSignal = true;
    }
  }

  // Visual error styling on input/textarea/select — red border / outline /
  // boxShadow ring. Limited to form fields to avoid false positives on
  // styled error toasts (already handled in alerts).
  const isFormField =
    el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' ||
    el.getAttribute('contenteditable') === 'true';
  if (isFormField) {
    const blob = `${style.borderColor} ${style.outlineColor} ${style.boxShadow}`;
    if (ERROR_COLOR_RE.test(blob)) out.visualErrorSignal = true;
    if (!out.visualErrorSignal) {
      // Sibling error span pattern: <input/> <span class="error">message</span>
      const sib = el.parentElement?.querySelector(
        '[class*="error" i], [class*="invalid" i], [class*="message" i]',
      );
      if (sib && sib !== el && (sib as HTMLElement).innerText && ERROR_TEXT_SIBLING_RE.test((sib as HTMLElement).innerText)) {
        out.visualErrorSignal = true;
      }
    }
  }

  return out;
}

function isDropdownTrigger(el: Element): boolean {
  const haspopup = el.getAttribute('aria-haspopup');
  if (haspopup === 'listbox' || haspopup === 'menu' || haspopup === 'true') return true;
  if (el.hasAttribute('data-radix-select-trigger')) return true;
  if (el.hasAttribute('data-headlessui-state')) return true;
  // aria-expanded alone catches accordions too — only treat as a dropdown
  // when paired with a library-class hint that distinguishes it from a
  // collapsible section.
  if (el.hasAttribute('aria-expanded')) {
    const cls = el.getAttribute('class') || '';
    if (/Select__|select-trigger|dropdown-trigger|combobox/i.test(cls)) return true;
  }
  return false;
}

function inferType(el: Element): string {
  const tag = el.tagName.toLowerCase();
  const role = el.getAttribute('role');
  const type = el.getAttribute('type');

  // Role-based inference
  if (role === 'tab') return 'tab';
  if (role === 'checkbox' || role === 'switch') return 'checkbox';
  if (role === 'radio') return 'radio';
  if (role === 'combobox') return 'select';
  // Dropdown triggers expressed as <button aria-haspopup="listbox"> etc.
  // Must come before the generic role/tag button branches so the LLM gets
  // `select` (and the `select_option` tool) instead of `button`.
  if (isDropdownTrigger(el)) return 'select';
  if (role === 'button') return 'button';
  if (role === 'link') return 'link';

  // Tag-based inference
  if (tag === 'button' || (tag === 'div' && role === 'button')) return 'button';
  if (tag === 'a') return 'link';
  if (tag === 'select') return 'select';
  if (tag === 'textarea') return 'textarea';
  if (tag === 'input') {
    if (type === 'checkbox') return 'checkbox';
    if (type === 'radio') return 'radio';
    if (type && SPECIFIC_INPUT_TYPES.includes(type)) return type;
    return 'input';
  }

  // Chart detection
  if (tag === 'canvas' || el.classList.contains('chart') ||
      el.querySelector('svg.recharts-surface, .highcharts-container, .chartjs-render-monitor')) {
    return 'chart';
  }

  // Table detection
  if (tag === 'table' || role === 'grid' || role === 'table') return 'table';

  return 'section';
}

function getLabel(el: HTMLElement): string {
  // P1: aria-labelledby beats aria-label when the labelledby refs resolve.
  // Composite labels ("First name", "Last name" pulled from a shared
  // legend) are common in dashboard headers and modal forms — prior code
  // missed them entirely.
  const labelledBy = resolveAriaLabelledBy(el);
  if (labelledBy) return labelledBy.slice(0, 100);
  return (
    el.getAttribute('aria-label') ||
    el.getAttribute('title') ||
    el.getAttribute('placeholder') ||
    el.getAttribute('alt') ||
    el.getAttribute('name') ||
    el.innerText?.trim().slice(0, 60) ||
    ''
  );
}

// ─── Option Capture ────────────────────────────────────────────────────────

function captureSelectOptions(select: HTMLSelectElement): OptionEntry[] {
  const options: OptionEntry[] = [];
  for (const opt of Array.from(select.options)) {
    if (opt.disabled || opt.value === '') continue;
    options.push({
      value: opt.value,
      label: opt.textContent?.trim() || opt.value,
    });
    if (options.length >= 30) break;
  }
  return options;
}

function captureCustomDropdownOptions(trigger: Element): OptionEntry[] | undefined {
  const listboxId = trigger.getAttribute('aria-controls') || trigger.getAttribute('aria-owns');
  const listbox = listboxId ? document.getElementById(listboxId) : null;
  if (!listbox) return undefined;

  const options: OptionEntry[] = [];
  const seen = new Set<string>();
  // Use the executor's option-selector list so the agent never tells the LLM
  // about an option the executor can't actually click.
  const optionEls = listbox.querySelectorAll(DROPDOWN_OPTION_SELECTORS.join(','));
  for (const opt of optionEls) {
    if ((opt as HTMLElement).getAttribute('aria-disabled') === 'true') continue;
    const text = (opt as HTMLElement).innerText?.trim() || '';
    const value = opt.getAttribute('data-value') || text;
    const label = text || value;
    if (!value || seen.has(label)) continue;
    seen.add(label);
    options.push({ value, label });
    if (options.length >= 30) break;
  }
  return options.length > 0 ? options : undefined;
}

// ─── Validation Capture ────────────────────────────────────────────────────

function captureValidation(el: Element): ValidationAttrs | null {
  const attrs: ValidationAttrs = {};
  let hasAny = false;

  if (el.hasAttribute('required') || el.getAttribute('aria-required') === 'true') {
    attrs.required = true;
    hasAny = true;
  }
  for (const attr of ['min', 'max', 'step', 'pattern'] as const) {
    const val = el.getAttribute(attr);
    if (val) {
      attrs[attr] = val;
      hasAny = true;
    }
  }
  const minLen = el.getAttribute('minlength');
  if (minLen) {
    attrs.minlength = parseInt(minLen, 10);
    hasAny = true;
  }
  const maxLen = el.getAttribute('maxlength');
  if (maxLen) {
    attrs.maxlength = parseInt(maxLen, 10);
    hasAny = true;
  }

  return hasAny ? attrs : null;
}

// ─── Card Detection ────────────────────────────────────────────────────────

function getCardLabel(card: Element): string | undefined {
  const heading = card.querySelector('h1, h2, h3, h4, [class*="title"], [class*="heading"]');
  const text = heading ? (heading as HTMLElement).innerText?.trim() : undefined;
  return text && text.length <= 80 ? text : undefined;
}

function captureCards(): PageSnapshot['cards'] {
  const cards: PageSnapshot['cards'] = [];
  const cardEls = document.querySelectorAll(
    '[class*="card"], article, [role="article"], [class*="Card"]',
  );

  cardEls.forEach((card) => {
    if (!isVisible(card as HTMLElement)) return;
    const label = getCardLabel(card) || '';
    if (!label) return;

    const elementSelectors: string[] = [];
    for (const sel of INTERACTIVE_SELECTORS) {
      card.querySelectorAll(sel).forEach((el) => {
        if (isVisible(el as HTMLElement)) {
          elementSelectors.push(generateSelector(el));
        }
      });
    }

    cards.push({
      selector: generateSelector(card),
      label: label.slice(0, 100),
      elements: elementSelectors.slice(0, 10),
    });
  });

  return cards.slice(0, 20);
}

// ─── Popover/Tooltip Detection ─────────────────────────────────────────────

function capturePopovers(): PageSnapshot['popovers'] {
  const popovers: PageSnapshot['popovers'] = [];
  const popoverSelectors = [
    '[role="tooltip"]',
    '[role="dialog"][class*="popover"]',
    '[popover]:popover-open',
    '[class*="popover"][class*="show"]',
    '[class*="tooltip"][class*="show"]',
    '[data-state="open"][data-radix-popper-content-wrapper]',
  ];

  for (const sel of popoverSelectors) {
    document.querySelectorAll(sel).forEach((el) => {
      if (!isVisible(el as HTMLElement)) return;
      const content = (el as HTMLElement).innerText?.trim().slice(0, 200);
      if (!content) return;

      const describedBy = el.id ? document.querySelector(`[aria-describedby="${el.id}"]`) : null;
      const controls = el.id ? document.querySelector(`[aria-controls="${el.id}"]`) : null;
      const trigger = describedBy || controls;

      popovers.push({
        triggerSelector: trigger ? generateSelector(trigger) : generateSelector(el),
        content,
      });
    });
  }

  return popovers.slice(0, 10);
}

// ─── Field Group Detection ─────────────────────────────────────────────────

function captureFieldGroups(): PageSnapshot['fieldGroups'] {
  const groups: PageSnapshot['fieldGroups'] = [];
  const groupNames = new Set<string>();

  document.querySelectorAll('input[type="radio"][name], input[type="checkbox"][name]').forEach((el) => {
    const name = el.getAttribute('name');
    if (name) groupNames.add(name);
  });

  for (const name of groupNames) {
    const inputs = document.querySelectorAll(`input[name="${CSS.escape(name)}"]`);
    if (inputs.length < 2) continue;

    const firstInput = inputs[0] as HTMLInputElement;
    const type = firstInput.type === 'radio' ? 'radio' : 'checkbox';

    const fieldset = firstInput.closest('fieldset');
    const legend = fieldset?.querySelector('legend');
    const groupLabel = legend?.textContent?.trim() || name;

    const options: Array<{ value: string; label: string; checked: boolean }> = [];
    inputs.forEach((input) => {
      const inputEl = input as HTMLInputElement;
      const labelEl = input.id ? document.querySelector(`label[for="${input.id}"]`) : null;
      const label = labelEl?.textContent?.trim() || inputEl.value;
      options.push({ value: inputEl.value, label, checked: inputEl.checked });
    });

    groups.push({
      name,
      type: type as 'radio' | 'checkbox',
      label: groupLabel,
      options: options.slice(0, 20),
    });
  }

  return groups.slice(0, 15);
}

// ─── Form Capture ──────────────────────────────────────────────────────────

function captureForms(): Array<{ id: string; fields: FormField[] }> {
  return captureFormsIn(document);
}

function captureFormsIn(root: Element | Document): Array<{ id: string; fields: FormField[] }> {
  const forms: Array<{ id: string; fields: FormField[] }> = [];

  root.querySelectorAll('form').forEach((form, i) => {
    const formId = form.id || form.getAttribute('name') || `form_${i}`;
    const fields: FormField[] = [];

    form.querySelectorAll('input, select, textarea').forEach((input) => {
      const name = input.getAttribute('name') || input.id || '';
      const tag = input.tagName.toLowerCase();
      const type = input.getAttribute('type') || tag;
      if (type === 'hidden' || type === 'submit') return;

      const labelEl = input.id ? document.querySelector(`label[for="${input.id}"]`) : null;
      const label =
        labelEl?.textContent?.trim() ||
        input.getAttribute('placeholder') ||
        input.getAttribute('aria-label') ||
        name;

      if (name || label) {
        const field: FormField = {
          name,
          type,
          label,
          selector: generateSelector(input),
        };

        // Capture options for <select>
        if (tag === 'select') {
          field.options = captureSelectOptions(input as HTMLSelectElement);
        }

        // Capture validation
        const validation = captureValidation(input);
        if (validation) field.validation = validation;

        // Capture checked state and group name for radio/checkbox
        if (tag === 'input') {
          const inputEl = input as HTMLInputElement;
          if (inputEl.type === 'checkbox' || inputEl.type === 'radio') {
            field.checked = inputEl.checked;
            const groupName = input.getAttribute('name');
            if (groupName) field.groupName = groupName;
          }
        }

        fields.push(field);
      }
    });

    if (fields.length > 0) {
      forms.push({ id: formId, fields });
    }
  });

  return forms;
}

// ─── Other Capture Functions ───────────────────────────────────────────────

function captureHeadings(): string[] {
  const headings: string[] = [];
  document.querySelectorAll('h1, h2, h3').forEach((h) => {
    const text = (h as HTMLElement).innerText?.trim();
    if (text && text.length < 200) {
      headings.push(`${h.tagName}: ${text}`);
    }
  });
  return headings.slice(0, 20);
}

function captureNavigation(): Array<{ text: string; href: string }> {
  const navLinks: Array<{ text: string; href: string }> = [];
  const seen = new Set<string>();

  const navContainers = document.querySelectorAll(
    'nav, [role="navigation"], aside, header, [class*="sidebar"], [class*="Sidebar"], [class*="nav"], [class*="Nav"], [class*="menu"], [class*="Menu"]',
  );

  const processLink = (a: Element) => {
    const href = a.getAttribute('href');
    const text = (a as HTMLElement).innerText?.trim();
    if (!href || !text || text.length > 50 || seen.has(href)) return;
    if (href.startsWith('#') || href.startsWith('javascript:') || href.startsWith('mailto:')) return;
    seen.add(href);
    navLinks.push({ text, href });
  };

  // Process <a> tags in nav containers
  navContainers.forEach((container) => {
    container.querySelectorAll('a[href]').forEach(processLink);
  });

  // Fallback: scan all links if nav containers yielded few results
  if (navLinks.length < 3) {
    document.querySelectorAll('a[href]').forEach(processLink);
  }

  // Also capture nav-like clickable items (buttons/divs in sidebars) that act as navigation
  // These are common in React/Next.js apps using client-side routing
  if (navLinks.length < 10) {
    navContainers.forEach((container) => {
      container.querySelectorAll('button, [role="link"], [role="menuitem"], [role="tab"]').forEach((el) => {
        const text = (el as HTMLElement).innerText?.trim();
        if (!text || text.length > 50 || text.length < 2) return;
        // Skip if it's clearly not a navigation item
        if (el.closest('form') || el.closest('table') || el.closest('[role="dialog"]')) return;
        const key = `click:${text}`;
        if (seen.has(key)) return;
        seen.add(key);
        // Use a pseudo-href format so the agent knows to click rather than navigate
        navLinks.push({ text, href: `click:${generateSelector(el)}` });
      });
    });
  }

  return navLinks.slice(0, 30);
}

function captureTables(): Array<{ headers: string[]; rowCount: number }> {
  const tables: Array<{ headers: string[]; rowCount: number }> = [];

  document.querySelectorAll('table, [role="grid"]').forEach((table) => {
    const headers: string[] = [];
    table.querySelectorAll('th, [role="columnheader"]').forEach((th) => {
      const text = (th as HTMLElement).innerText?.trim();
      if (text) headers.push(text);
    });
    const rowCount = table.querySelectorAll('tr, [role="row"]').length;
    if (headers.length > 0) {
      tables.push({ headers, rowCount });
    }
  });

  return tables;
}

// ─── Utilities ─────────────────────────────────────────────────────────────

function isVisible(el: HTMLElement): boolean {
  if (!el.offsetParent && el.style.position !== 'fixed' && el.style.position !== 'sticky') return false;
  const style = getComputedStyle(el);
  return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
}

// ─── Post-Action Screen Observation ────────────────────────────────────────

/**
 * Lightweight scan that runs after every state-changing iframe command
 * (click / type / navigate / press-key / scroll-to). Surfaces what's
 * actually visible on screen so the LLM stops narrating into the void
 * when an action lands on an error page, fires a permission denial toast,
 * or leaves a validation error next to the input it just filled.
 */
export interface ScreenObservation {
  url: string;
  title: string;
  h1?: string;
  alerts: Array<{ role: string; text: string; severity: 'error' | 'warn' | 'info' }>;
  validationErrors: Array<{ near: string; text: string }>;
  modal?: { title: string; bodyText: string };
  unexpectedTextHits: string[];
  /**
   * Slim per-form snapshot — only present when the page actually has visible
   * forms. Lets the server's learning loop diff what's on screen against
   * what the PDP's screen.elements describes. Capped at 2 forms × 12 fields
   * to keep the per-turn payload small (~1-2KB worst case for a busy form).
   */
  forms?: Array<{ id: string; fields: FormField[] }>;
  /**
   * Phase 2 (#A8, #A9, #B22). Page-level loading / empty-state hint, derived
   * from skeleton/progress/route-loader patterns. Lets the prompt builder
   * suppress narrate-on-arrival when the page hasn't actually loaded yet.
   */
  loadingState?: 'idle' | 'skeleton' | 'progress' | 'pending_request' | 'empty_state';
  /**
   * Phase 2 (#B13, #A1, #A2). Counts of visual / dynamic-state signals
   * surfaced this turn — lets the prompt VISUAL SIGNALS block render a
   * summary line without the agent re-walking the snapshot.
   */
  visualSignalCounts?: {
    visualErrors: number;
    loadingButtons: number;
    pointerBlocked: number;
    transformHidden: number;
  };
  /**
   * Phase 1 — network awareness. The fetch/XHR shim's last few events plus
   * any ambient signals (offline, captcha, shim-disabled) drained since the
   * previous observation. Server dedupes events by `id`. Status is the
   * derived precedence-ordered flag (see network-events.ts:NetworkStatus).
   * Always present so the server can tell "no events" from "shim disabled".
   */
  network?: {
    status:
      | 'idle'
      | 'pending'
      | 'failed'
      | 'rate_limited'
      | 'auth_expired'
      | 'offline'
      | 'captcha_pending'
      | 'disabled';
    lastEventId: number;
    recent: Array<{
      id: number;
      ts: number;
      method: string;
      url: string;
      status: number;
      latencyMs: number;
      responseSize?: number;
      bodyExcerpt?: string;
      retryAfterMs?: number;
      redirectedToLogin?: boolean;
      initiator: 'fetch' | 'xhr' | 'beacon';
    }>;
    ambient: Array<{
      kind:
        | 'shim-disabled'
        | 'offline'
        | 'online'
        | 'captcha-detected'
        | 'captcha-cleared'
        | 'pending-stuck';
      ts: number;
      detail?: Record<string, unknown>;
    }>;
  };
}

const ALERT_SELECTORS = [
  '[role="alert"]',
  '[role="status"]',
  '[aria-live="assertive"]',
  '[aria-live="polite"]',
  '.alert',
  '.error',
  '.notification',
  '.toast',
  '[class*="Toast"]',
  '[class*="Alert"]',
  '[class*="error"]',
  '[class*="Error"]',
  '[class*="danger"]',
  '[class*="banner"]',
  '[class*="Banner"]',
  // Phase 2 (#B13). Common toast / snackbar libraries that don't use
  // role=alert by default. Paired with the existing patterns so duplicates
  // are deduped by the `seen` set in the capture pass.
  '[class*="snackbar" i]',
  '[class*="notification" i]',
  '.Toastify__toast',
  '.go-toast',
  '.sonner-toast',
  '[data-sonner-toast]',
  '[data-status="error"]',
  '[data-status="warning"]',
];

// Visible body text matching any of these is surfaced as an "unexpectedTextHit"
// when no alert/validation hit fired. Kept tight to avoid false positives —
// these are phrases that almost always indicate something went wrong.
const UNEXPECTED_TEXT_PATTERNS: Array<{ pattern: RegExp; label: string }> = [
  { pattern: /\b(unauthor[iz]?ed|forbidden|access denied|permission denied)\b/i, label: 'authorization' },
  { pattern: /\b(invalid (credentials|password|email|token)|incorrect (password|email))\b/i, label: 'invalid_credentials' },
  { pattern: /\b(requires admin|admin (only|access required)|you (don'?t|do not) have (permission|access))\b/i, label: 'requires_admin' },
  { pattern: /\b(something went wrong|an error occurred|unexpected error|failed to (load|fetch|save))\b/i, label: 'generic_error' },
  { pattern: /\b(404|page not found|not found)\b/i, label: 'not_found' },
  { pattern: /\b(500|internal server error|service unavailable|bad gateway)\b/i, label: 'server_error' },
];

const ALERT_TEXT_CAP = 240;

/**
 * Wait for an async modal/dialog/drawer to render in the DOM, with a tight
 * budget. Modals frequently mount 100-400ms after the click that opens them
 * (Radix portal mounts, Headless UI Transition, MUI Modal animation). The
 * synchronous post-action sweep would miss them and the agent narrates over
 * the wrong screen. Resolves true on first match, false on timeout.
 *
 * Uses {@link MODAL_ATTACH_SELECTORS} — same list as captureOpenModals so
 * the wait + the capture see the same surface.
 */
const MODAL_ATTACH_SELECTORS = [
  '[role="dialog"]',
  '[aria-modal="true"]',
  'dialog[open]',
  '[class*="modal"][class*="open"]',
  '[class*="modal"][class*="show"]',
  '[class*="drawer"][class*="open"]',
  '[class*="dialog"][class*="open"]',
];

export async function waitForAsyncModal(timeoutMs = 800): Promise<boolean> {
  // Fast path — already open.
  if (MODAL_ATTACH_SELECTORS.some((s) => {
    try { return document.querySelector(s) !== null; } catch { return false; }
  })) {
    return true;
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = (val: boolean): void => {
      if (settled) return;
      settled = true;
      observer.disconnect();
      clearTimeout(timer);
      resolve(val);
    };
    const timer = setTimeout(() => finish(false), timeoutMs);
    const observer = new MutationObserver(() => {
      const hit = MODAL_ATTACH_SELECTORS.some((s) => {
        try { return document.querySelector(s) !== null; } catch { return false; }
      });
      if (hit) finish(true);
    });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'open', 'aria-modal'] });
  });
}

/**
 * Async variant of {@link extractScreenObservation} that first waits up to
 * `modalWaitMs` for an async modal to render. Use this after any action
 * that plausibly opens a dialog (click, key:Enter). Falls back to the sync
 * path immediately when no modal appears within the budget.
 */
export async function extractScreenObservationAsync(modalWaitMs = 800): Promise<ScreenObservation> {
  await waitForAsyncModal(modalWaitMs);
  return extractScreenObservation();
}

export function extractScreenObservation(): ScreenObservation {
  const obs: ScreenObservation = {
    url: location.pathname + location.search,
    title: document.title,
    alerts: [],
    validationErrors: [],
    unexpectedTextHits: [],
  };

  const h1El = document.querySelector('h1');
  if (h1El) {
    const text = (h1El as HTMLElement).innerText?.trim();
    if (text) obs.h1 = text.slice(0, 160);
  }

  const seen = new Set<Element>();
  for (const sel of ALERT_SELECTORS) {
    let matched: Element[];
    try {
      // Pierce shadow roots — toast portals from MUI/Radix/etc. frequently
      // attach inside shadow DOM and were invisible to the prior scan.
      matched = querySelectorAllDeep(sel);
    } catch {
      continue; // skip malformed selectors defensively
    }
    matched.forEach((el) => {
      if (seen.has(el) || !isVisible(el as HTMLElement)) return;
      // Skip nested matches — outer element wins.
      for (const prior of seen) {
        if (prior.contains(el)) return;
      }
      seen.add(el);
      const text = (el as HTMLElement).innerText?.trim();
      if (!text || text.length < 2) return;
      const role = el.getAttribute('role') ?? inferAlertRoleFromClass(el);
      obs.alerts.push({
        role: role || 'alert',
        text: text.slice(0, ALERT_TEXT_CAP),
        severity: inferSeverity(el, text),
      });
      if (obs.alerts.length >= 6) return;
    });
    if (obs.alerts.length >= 6) break;
  }

  document.querySelectorAll('[aria-invalid="true"]').forEach((el) => {
    if (!isVisible(el as HTMLElement)) return;
    const near = (el as HTMLElement).getAttribute('name') ||
      (el as HTMLElement).getAttribute('aria-label') ||
      (el as HTMLElement).getAttribute('placeholder') ||
      generateSelector(el);
    const describedById = el.getAttribute('aria-describedby');
    let message = '';
    if (describedById) {
      const desc = document.getElementById(describedById);
      if (desc && isVisible(desc)) message = desc.innerText?.trim() ?? '';
    }
    if (!message) {
      // Look for an adjacent error/help element.
      const sibling = el.parentElement?.querySelector('[class*="error"], [class*="Error"], [role="alert"]');
      if (sibling && sibling !== el && isVisible(sibling as HTMLElement)) {
        message = (sibling as HTMLElement).innerText?.trim() ?? '';
      }
    }
    if (message) {
      obs.validationErrors.push({ near: near.slice(0, 80), text: message.slice(0, ALERT_TEXT_CAP) });
    }
    if (obs.validationErrors.length >= 6) return;
  });

  // Reuse existing modal capture — only surface the topmost visible modal,
  // and only its title + body text (interactive elements are already in the
  // periodic snapshot).
  const modals = captureOpenModals();
  if (modals.length > 0) {
    const top = modals[modals.length - 1];
    const modalEl = document.querySelector(top.trigger);
    const bodyText = modalEl ? ((modalEl as HTMLElement).innerText?.trim() ?? '') : '';
    obs.modal = { title: top.title, bodyText: bodyText.slice(0, ALERT_TEXT_CAP) };
  }

  // Body-text fallback — only run if nothing more specific fired.
  if (obs.alerts.length === 0 && obs.validationErrors.length === 0) {
    const bodyText = (document.body?.innerText ?? '').slice(0, 8000);
    const seenLabels = new Set<string>();
    for (const { pattern, label } of UNEXPECTED_TEXT_PATTERNS) {
      if (seenLabels.has(label)) continue;
      const m = bodyText.match(pattern);
      if (m) {
        seenLabels.add(label);
        obs.unexpectedTextHits.push(`${label}: "${m[0]}"`);
      }
      if (obs.unexpectedTextHits.length >= 4) break;
    }
  }

  // Slim forms snapshot — feeds the server's learning loop so it can diff
  // observed fields against the PDP's screen.elements. Capped to keep the
  // per-turn payload small; modal forms (often the most action-relevant)
  // win when both the page and a modal carry forms.
  const allForms: Array<{ id: string; fields: FormField[] }> = [];
  const modalForms = captureOpenModals().flatMap((m) => m.forms);
  if (modalForms.length > 0) allForms.push(...modalForms);
  if (allForms.length < 2) allForms.push(...captureForms());
  if (allForms.length > 0) {
    obs.forms = allForms.slice(0, 2).map((f) => ({
      id: f.id,
      fields: f.fields.slice(0, 12),
    }));
  }

  // Phase 1: network awareness. Drained ambient signals + last 5 events. The
  // server dedupes by event id (we always include the last 5 — if nothing
  // new happened this snapshot the server simply sees the same ids and
  // skips). Ambient signals (offline/captcha/shim-disabled) ARE drained
  // here so they're delivered exactly once per snapshot turn.
  try {
    const status = computeNetworkStatusFromShim();
    obs.network = {
      status,
      lastEventId: getLastNetworkEventIdFromShim(),
      recent: drainNetworkEventsFromShim(5),
      ambient: drainAmbientSignalsFromShim(),
    };
  } catch {
    // Network shim was never installed (non-iframe / older widget); leave undefined.
  }

  // Phase 2: page-level loading state + visual signal counts. Single DOM
  // pass — bound by selector cost, not element count. The per-element
  // signals already live on MappedElement (used by the periodic snapshot);
  // this is the post-action summary the prompt builder branches on.
  obs.loadingState = detectPageLoadingState(obs.network?.status);
  obs.visualSignalCounts = countVisualSignals();

  return obs;
}

const SKELETON_SELECTOR =
  '[class*="skeleton" i], [class*="Skeleton"], [class*="shimmer" i], [data-state="loading"] > [class*="placeholder"]';
const PROGRESS_SELECTOR =
  '[role="progressbar"], #nprogress, [class*="top-loader" i], [class*="page-loader" i]';
const EMPTY_STATE_SELECTOR =
  '[class*="empty-state" i], [data-state="empty"], [data-empty="true"]';

function detectPageLoadingState(
  netStatus: NonNullable<ScreenObservation['network']>['status'] | undefined,
): NonNullable<ScreenObservation['loadingState']> {
  // Network status takes precedence — pending_request is more specific than
  // visible spinners (those might be unrelated decorations).
  if (netStatus === 'pending') return 'pending_request';
  try {
    if (document.querySelector(PROGRESS_SELECTOR)) return 'progress';
  } catch { /* malformed selector — should not happen */ }
  try {
    const skel = document.querySelectorAll(SKELETON_SELECTOR);
    if (skel.length >= 3) return 'skeleton'; // 1-2 isolated skeletons are decoration; ≥3 is a loading list
  } catch { /* ignore */ }
  try {
    if (document.querySelector(EMPTY_STATE_SELECTOR)) return 'empty_state';
  } catch { /* ignore */ }
  return 'idle';
}

function countVisualSignals(): NonNullable<ScreenObservation['visualSignalCounts']> {
  const counts = { visualErrors: 0, loadingButtons: 0, pointerBlocked: 0, transformHidden: 0 };
  // Coarse pass — count visible inputs with a sibling error span (the
  // dominant silent-failure pattern). The per-element MappedElement pass
  // catches the rest, but a screen-level summary helps the agent prioritise.
  try {
    const errorSibs = document.querySelectorAll(
      'input + [class*="error" i], textarea + [class*="error" i], [class*="invalid"]:not(input):not(textarea)',
    );
    counts.visualErrors = Math.min(errorSibs.length, 10);
  } catch { /* ignore */ }
  try {
    const loadingButtons = document.querySelectorAll(
      'button[data-loading="true"], button[aria-busy="true"], button [class*="spin" i]',
    );
    counts.loadingButtons = Math.min(loadingButtons.length, 10);
  } catch { /* ignore */ }
  return counts;
}

// Network-shim accessors are imported at the top of this file. We keep a
// per-module watermark so each observation only re-emits events that the
// previous snapshot didn't cover. The element-mapper is a singleton in the
// iframe — one watermark per iframe is correct.
let _netLastDrainedId = 0;
function drainNetworkEventsFromShim(limit: number): NonNullable<ScreenObservation['network']>['recent'] {
  const evs = drainNetworkEventsSinceFromShim(_netLastDrainedId, limit);
  if (evs.length > 0) _netLastDrainedId = evs[evs.length - 1].id;
  return evs;
}

function inferAlertRoleFromClass(el: Element): string {
  const cls = (el.className || '').toString().toLowerCase();
  if (cls.includes('toast')) return 'toast';
  if (cls.includes('banner')) return 'banner';
  if (cls.includes('notification')) return 'notification';
  if (cls.includes('error')) return 'error';
  if (cls.includes('alert')) return 'alert';
  return 'status';
}

function inferSeverity(el: Element, text: string): 'error' | 'warn' | 'info' {
  const cls = (el.className || '').toString().toLowerCase();
  const aria = (el.getAttribute('aria-live') || '').toLowerCase();
  const blob = `${cls} ${text.toLowerCase()}`;
  if (
    cls.includes('error') ||
    cls.includes('danger') ||
    cls.includes('destructive') ||
    cls.includes('fail') ||
    aria === 'assertive' ||
    /\b(error|denied|unauthor|forbidden|invalid|failed|cannot)\b/.test(blob)
  ) {
    return 'error';
  }
  if (cls.includes('warn') || cls.includes('caution') || /\bwarning\b/.test(blob)) {
    return 'warn';
  }
  return 'info';
}

/**
 * True iff the observation has no signal worth surfacing to the LLM.
 * Lets the prompt builder suppress empty POST-ACTION SCREEN STATE blocks
 * to keep token budget unchanged on uneventful actions.
 */
export function isObservationEmpty(obs: ScreenObservation, prevUrl: string | null): boolean {
  // Network status is non-trivial when it's anything other than 'idle' or
  // 'disabled' — both of those are the agent's default ("no requests yet"
  // and "shim never installed"), neither warrants a prompt block.
  const netInteresting =
    !!obs.network &&
    obs.network.status !== 'idle' &&
    obs.network.status !== 'disabled' &&
    (obs.network.recent.length > 0 || obs.network.ambient.length > 0);
  // Phase 2: visual / loading signals also count as non-empty.
  const visualInteresting =
    (obs.loadingState && obs.loadingState !== 'idle') ||
    !!(obs.visualSignalCounts &&
      (obs.visualSignalCounts.visualErrors > 0 || obs.visualSignalCounts.loadingButtons > 0));
  return (
    obs.alerts.length === 0 &&
    obs.validationErrors.length === 0 &&
    obs.unexpectedTextHits.length === 0 &&
    !obs.modal &&
    !netInteresting &&
    !visualInteresting &&
    (prevUrl === null || prevUrl === obs.url)
  );
}
